import { BaseService, type ServiceResponse } from '@/services/api/base.service';
import { expandRecurringBlock } from '@/utils/coaching/computeAvailableSlots';
import type {
  CoachingSession,
  SessionFilters,
  CreateSessionInput,
  UpdateSessionInput,
  SkillAssessment,
  CreateSkillAssessmentInput,
  BookingRequest,
  BookingRequestStatus,
  CreateBookingRequestInput,
  RespondBookingRequestInput,
  CoachTimeBlock,
  CreateTimeBlockInput,
  CreateGroupSessionInput,
  SessionParticipant,
  CoachNote,
  CreateCoachNoteInput,
  UpdateCoachNoteInput,
  CoachWeeklyAvailability,
  UpsertWeeklyAvailabilityInput,
} from '@/types/coaching.types';

export class SessionsService extends BaseService {
  /**
   * Send a coaching push notification
   */
  async sendCoachingPush(params: {
    userId: string;
    title: string;
    body: string;
    type: 'coaching_session' | 'coaching_booked' | 'coaching_cancelled' | 'coaching_reminder';
    data?: Record<string, unknown>;
  }): Promise<void> {
    try {
      const { error } = await this.supabase.functions.invoke('send-push-notification', {
        body: {
          user_ids: [params.userId],
          title: params.title,
          body: params.body,
          data: {
            type: params.type,
            ...params.data
          }
        }
      });

      if (error) {
        console.error('[Coaching] Push notification failed:', error);
      } else {
        console.log(`[Coaching] Push notification sent to ${params.userId}`);
      }
    } catch (error) {
      console.error('[Coaching] Error sending push notification:', error);
    }
  }

  async nudgeStudent(studentId: string, coachName: string): Promise<void> {
    return this.sendCoachingPush({
      userId: studentId,
      title: `${coachName} sent you a nudge`,
      body: 'Time to get back to your practice!',
      type: 'coaching_reminder',
      data: { action: 'nudge' },
    });
  }

  /**
   * Get coaching sessions with optional filters
   * Returns sessions based on authenticated user context (coach or player)
   */
  async getSessions(filters?: SessionFilters): Promise<ServiceResponse<CoachingSession[]>> {
    return this.withErrorHandling(async () => {
      let query = this.supabase
        .from('coaching_sessions')
        .select(`
          *,
          coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience,
            max_students,
            accepts_new_students
          ),
          player:profiles!coaching_sessions_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          )
        `)
        .order('scheduled_at', { ascending: true });

      // Apply filters if provided
      if (filters?.coachProfileId) {
        query = query.eq('coach_id', filters.coachProfileId);
      }

      if (filters?.playerId) {
        query = query.eq('player_id', filters.playerId);
      }

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.startDate) {
        query = query.gte('scheduled_at', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('scheduled_at', filters.endDate);
      }

      if (filters?.sessionType) {
        query = query.eq('session_type', filters.sessionType);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as CoachingSession[];
    });
  }

  /**
   * Get a single coaching session with full details
   */
  async getSession(id: string): Promise<ServiceResponse<CoachingSession>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coaching_sessions')
        .select(`
          *,
          coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience,
            max_students,
            accepts_new_students
          ),
          player:profiles!coaching_sessions_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as CoachingSession;
    });
  }

  /**
   * Get upcoming sessions for a coach (future only, for lightweight callers)
   */
  async getUpcomingSessions(
    coachProfileId: string,
    limit: number = 10
  ): Promise<ServiceResponse<CoachingSession[]>> {
    return this.withErrorHandling(async () => {
      const now = new Date().toISOString();

      const { data, error } = await this.supabase
        .from('coaching_sessions')
        .select(`
          *,
          coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience,
            max_students,
            accepts_new_students
          ),
          player:profiles!coaching_sessions_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          ),
          participants:coaching_session_participants(
            id,
            player_id,
            status,
            player:profiles!coaching_session_participants_player_id_fkey(
              id,
              display_name,
              avatar_url
            )
          )
        `)
        .eq('coach_id', coachProfileId)
        .gte('scheduled_at', now)
        .in('status', ['scheduled', 'confirmed'])
        .order('scheduled_at', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return data as CoachingSession[];
    });
  }

  /**
   * Get all sessions for a coach from Jan 1 of the current year through end of year.
   * Used by the schedule page calendar so past days are fully populated.
   */
  async getYearToDateSessions(
    coachProfileId: string
  ): Promise<ServiceResponse<CoachingSession[]>> {
    return this.withErrorHandling(async () => {
      const year = new Date().getFullYear();
      const yearStart = new Date(year, 0, 1).toISOString();
      const yearEnd   = new Date(year, 11, 31, 23, 59, 59).toISOString();

      const { data, error } = await this.supabase
        .from('coaching_sessions')
        .select(`
          *,
          coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience,
            max_students,
            accepts_new_students
          ),
          player:profiles!coaching_sessions_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          ),
          participants:coaching_session_participants(
            id,
            player_id,
            status,
            player:profiles!coaching_session_participants_player_id_fkey(
              id,
              display_name,
              avatar_url
            )
          )
        `)
        .eq('coach_id', coachProfileId)
        .gte('scheduled_at', yearStart)
        .lte('scheduled_at', yearEnd)
        .order('scheduled_at', { ascending: true });

      if (error) throw error;
      return data as CoachingSession[];
    });
  }

  /**
   * Get recent past sessions for a coach that have no post_notes (need closeout).
   * Looks back up to 30 days.
   */
  async getRecentUnclosedSessions(
    coachProfileId: string,
    limit: number = 20
  ): Promise<ServiceResponse<CoachingSession[]>> {
    return this.withErrorHandling(async () => {
      const now = new Date();
      const cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const nowIso = now.toISOString();

      const { data, error } = await this.supabase
        .from('coaching_sessions')
        .select(`
          *,
          player:profiles!coaching_sessions_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          )
        `)
        .eq('coach_id', coachProfileId)
        .in('status', ['completed', 'confirmed'])
        .gte('scheduled_at', cutoff)
        .lt('scheduled_at', nowIso)
        .is('post_notes', null)
        .order('scheduled_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as CoachingSession[];
    });
  }

  /**
   * Get session history for a player
   */
  async getSessionHistory(
    playerId: string,
    limit: number = 20
  ): Promise<ServiceResponse<CoachingSession[]>> {
    return this.withErrorHandling(async () => {
      const sessionSelect = `
        *,
        coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(
          id,
          user_id,
          bio,
          specialties,
          user:profiles!fk_coach_profiles_user_profile(
            id,
            display_name,
            avatar_url
          )
        ),
        player:profiles!coaching_sessions_player_id_fkey(
          id,
          display_name,
          avatar_url,
          email
        )
      `;

      // Fetch direct (1-on-1) sessions
      const { data: directSessions, error: directError } = await this.supabase
        .from('coaching_sessions')
        .select(sessionSelect)
        .eq('player_id', playerId)
        .order('scheduled_at', { ascending: false })
        .limit(limit);

      if (directError) throw directError;

      // Fetch group sessions where this player is a participant (but not primary player_id)
      const { data: participantRows, error: participantError } = await this.supabase
        .from('coaching_session_participants')
        .select(`session:coaching_sessions(${sessionSelect})`)
        .eq('player_id', playerId);

      if (participantError) throw participantError;

      const groupSessions = (participantRows ?? [])
        .map((row: any) => row.session)
        .filter(Boolean)
        // exclude sessions already in directSessions (player was primary too)
        .filter((gs: any) => !(directSessions ?? []).some((ds: any) => ds.id === gs.id));

      const all = [...(directSessions ?? []), ...groupSessions]
        .sort((a: any, b: any) =>
          new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()
        )
        .slice(0, limit);

      return all as CoachingSession[];
    });
  }

  /**
   * Create a new coaching session (booking)
   */
  async createSession(input: CreateSessionInput): Promise<ServiceResponse<CoachingSession>> {
    return this.withErrorHandling(async () => {
      // Conflict check before INSERT
      const reqStart = new Date(input.scheduled_at);
      await this.checkCoachConflict(input.coach_id, reqStart, input.duration_mins || 60);

      // Fetch coach's hourly rate to calculate session price
      const { data: coachProfile, error: coachProfileError } = await this.supabase
        .from('coach_profiles')
        .select('hourly_rate')
        .eq('id', input.coach_id)
        .single();

      if (coachProfileError) {
        console.warn('Could not fetch coach hourly rate:', coachProfileError);
      }

      const hourlyRate = coachProfile?.hourly_rate || 0;
      const durationMins = input.duration_mins || 60;
      const priceCents = Math.round(hourlyRate * (durationMins / 60));
      const paymentStatus = priceCents > 0 ? 'pending' : 'free';

      const { data, error } = await this.supabase
        .from('coaching_sessions')
        .insert({
          coach_id: input.coach_id,
          player_id: input.player_id,
          club_id: input.club_id ?? null,
          relationship_id: input.relationship_id,
          session_type: input.session_type,
          scheduled_at: input.scheduled_at,
          duration_mins: durationMins,
          location: input.location || null,
          status: 'scheduled',
          focus_areas: input.focus_areas || [],
          pre_notes: input.pre_notes || null,
          price_cents: priceCents > 0 ? priceCents : null,
          payment_status: paymentStatus,
          currency: 'EUR',
        })
        .select(`
          *,
          coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience,
            max_students,
            accepts_new_students
          ),
          player:profiles!coaching_sessions_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          )
        `)
        .single();

      if (error) throw error;

      const sessionData = data as CoachingSession;

      // Send confirmation email (with ICS) + push to player and coach
      await this.supabase.functions.invoke('send-booking-confirmation', {
        body: { session_id: sessionData.id },
      }).catch(err => console.warn('send-booking-confirmation failed (non-fatal):', err));

      return sessionData;
    });
  }

  /**
   * Update an existing coaching session
   */
  async updateSession(
    id: string,
    input: UpdateSessionInput
  ): Promise<ServiceResponse<CoachingSession>> {
    return this.withErrorHandling(async () => {
      const updateData: Record<string, unknown> = {};

      if (input.status !== undefined) updateData.status = input.status;
      if (input.pre_notes !== undefined) updateData.pre_notes = input.pre_notes;
      if (input.post_notes !== undefined) updateData.post_notes = input.post_notes;
      if (input.player_visible_notes !== undefined) updateData.player_visible_notes = input.player_visible_notes;
      if (input.focus_areas !== undefined) updateData.focus_areas = input.focus_areas;
      if (input.rating !== undefined) updateData.rating = input.rating;
      if (input.scheduled_at !== undefined) updateData.scheduled_at = input.scheduled_at;
      if (input.duration_mins !== undefined) updateData.duration_mins = input.duration_mins;
      if (input.location !== undefined) updateData.location = input.location;
      if (input.session_type !== undefined) updateData.session_type = input.session_type;
      if (input.cancelled_reason !== undefined) updateData.cancelled_reason = input.cancelled_reason;

      updateData.updated_at = new Date().toISOString();

      const { data, error } = await this.supabase
        .from('coaching_sessions')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience,
            max_students,
            accepts_new_students
          ),
          player:profiles!coaching_sessions_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          )
        `)
        .maybeSingle();

      if (error) throw error;
      // RLS-blocked UPDATEs return 200 with zero rows and `null` data — caught
      // here so the caller sees a real failure instead of a silent no-op.
      if (!data) {
        throw new Error(
          `Update to coaching_sessions ${id} affected 0 rows. ` +
          `Likely an RLS policy is blocking the write for the current user.`
        );
      }
      return data as CoachingSession;
    });
  }

  /**
   * Cancel a coaching session
   */
  async cancelSession(id: string, reason: string): Promise<ServiceResponse<CoachingSession>> {
    return this.withErrorHandling(async () => {
      // Fetch session + coach user_id to determine if caller is coach or player
      const { data: session, error: fetchErr } = await this.supabase
        .from('coaching_sessions')
        .select('id, scheduled_at, coach_id, player_id, coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(user_id)')
        .eq('id', id)
        .single();
      if (fetchErr) throw fetchErr;

      const { data: authData } = await this.supabase.auth.getUser();
      const userId = authData.user?.id;

      // Check if caller is the coach or a club admin (both bypass the cancellation window)
      const isCoach = (session.coach_profile as { user_id: string } | null)?.user_id === userId;
      let isAdmin = false;
      if (!isCoach && userId) {
        const { data: membership } = await this.supabase
          .from('user_club_memberships')
          .select('role')
          .eq('user_id', userId)
          .in('role', ['club_admin', 'super_admin'])
          .maybeSingle();
        isAdmin = !!membership;
      }

      // Enforce cancellation window for players only
      if (!isCoach && !isAdmin) {
        const { data: policies } = await this.supabase
          .from('coach_settings')
          .select('cancellation_window_hours')
          .eq('coach_id', session.coach_id)
          .maybeSingle();
        const windowHours = policies?.cancellation_window_hours ?? 24;
        const hoursUntil = (new Date(session.scheduled_at).getTime() - Date.now()) / 3_600_000;
        if (hoursUntil > 0 && hoursUntil < windowHours) {
          const hoursLeft = Math.floor(hoursUntil);
          const timeLeft = hoursLeft < 1
            ? 'less than an hour'
            : hoursLeft === 1
            ? '1 hour'
            : `${hoursLeft} hours`;
          throw new Error(
            `Your coach's cancellation policy requires at least ${windowHours} hours' notice. ` +
            `This session is in ${timeLeft}.`
          );
        }
      }

      const { data, error } = await this.supabase
        .from('coaching_sessions')
        .update({
          status: 'cancelled',
          cancelled_reason: reason,
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select(`
          *,
          coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience,
            max_students,
            accepts_new_students
          ),
          player:profiles!coaching_sessions_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          )
        `)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        throw new Error(
          `Cancel of coaching_sessions ${id} affected 0 rows. ` +
          `Likely an RLS policy is blocking the write for the current user.`
        );
      }
      // Send cancellation email + push to player (and coach if cancelled by player)
      const sessionData = data as CoachingSession;
      await this.supabase.functions.invoke('send-cancellation-notification', {
        body: {
          session_id: sessionData.id,
          reason: reason || null,
          cancelled_by: isCoach ? 'coach' : 'player',
        },
      }).catch(err => console.warn('send-cancellation-notification failed (non-fatal):', err));

      return sessionData;
    });
  }

  /**
   * Create a skill assessment for a player
   */
  async createSkillAssessment(
    input: CreateSkillAssessmentInput
  ): Promise<ServiceResponse<SkillAssessment>> {
    return this.withErrorHandling(async () => {
      // Map current authenticated coach to a coach_profile
      const { data: userData, error: authError } = await this.supabase.auth.getUser();
      if (authError || !userData.user) {
        throw new Error('User not authenticated');
      }

      // If club_id is provided, find the coach profile for that club
      // Otherwise, find the independent coach profile (club_id IS NULL)
      let coachProfileQuery = this.supabase
        .from('coach_profiles')
        .select('id')
        .eq('user_id', userData.user.id);

      if (input.club_id) {
        coachProfileQuery = coachProfileQuery.eq('club_id', input.club_id);
      } else {
        coachProfileQuery = coachProfileQuery.is('club_id', null);
      }

      const { data: coachProfile, error: coachProfileError } = await coachProfileQuery.single();

      if (coachProfileError || !coachProfile) {
        throw new Error('Coach profile not found');
      }

      const { data, error } = await this.supabase
        .from('skill_assessments')
        .insert({
          player_id: input.player_id,
          coach_id: coachProfile.id,
          club_id: input.club_id ?? null,
          session_id: input.session_id || null,
          assessed_at: new Date().toISOString(),
          driving: input.driving,
          iron_play: input.iron_play,
          short_game: input.short_game,
          putting: input.putting,
          bunker: input.bunker,
          mental: input.mental,
          driver_distance: input.driver_distance ?? null,
          driver_accuracy: input.driver_accuracy ?? null,
          approach_gir: input.approach_gir ?? null,
          up_and_down: input.up_and_down ?? null,
          putts_per_round: input.putts_per_round ?? null,
          notes: input.notes ?? null,
        })
        .select(`
          *,
          coach_profile:coach_profiles!skill_assessments_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience
          ),
          player:profiles!skill_assessments_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          ),
          session:coaching_sessions(
            id,
            session_type,
            scheduled_at
          )
        `)
        .single();

      if (error) throw error;
      return data as SkillAssessment;
    });
  }

  /**
   * Get all skill assessments for a player
   */
  async getPlayerAssessments(playerId: string): Promise<ServiceResponse<SkillAssessment[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('skill_assessments')
        .select(`
          *,
          coach_profile:coach_profiles!skill_assessments_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience
          ),
          player:profiles!skill_assessments_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          ),
          session:coaching_sessions(
            id,
            session_type,
            scheduled_at
          )
        `)
        .eq('player_id', playerId)
        .order('assessed_at', { ascending: false });

      if (error) throw error;
      return data as SkillAssessment[];
    });
  }

  /**
   * Get the most recent skill assessment for a player
   */
  async getLatestAssessment(playerId: string): Promise<ServiceResponse<SkillAssessment>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('skill_assessments')
        .select(`
          *,
          coach_profile:coach_profiles!skill_assessments_coach_id_fkey(
            id,
            user_id,
            club_id,
            bio,
            specialties,
            certifications,
            hourly_rate,
            years_experience
          ),
          player:profiles!skill_assessments_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          ),
          session:coaching_sessions(
            id,
            session_type,
            scheduled_at
          )
        `)
        .eq('player_id', playerId)
        .order('assessed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as SkillAssessment;
    });
  }
  // ==========================================================================
  // Booking Requests
  // ==========================================================================

  async getBookingRequests(
    coachProfileId: string,
    status?: BookingRequestStatus
  ): Promise<ServiceResponse<BookingRequest[]>> {
    return this.withErrorHandling(async () => {
      let query = this.supabase
        .from('coaching_booking_requests')
        .select(`
          *,
          player:profiles!coaching_booking_requests_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          ),
          session:coaching_sessions!coaching_booking_requests_session_id_fkey(
            payment_status
          )
        `)
        .eq('coach_profile_id', coachProfileId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as BookingRequest[];
    });
  }

  async respondToBookingRequest(
    requestId: string,
    input: RespondBookingRequestInput
  ): Promise<ServiceResponse<BookingRequest>> {
    return this.withErrorHandling(async () => {
      // First get the request details
      const { data: request, error: fetchError } = await this.supabase
        .from('coaching_booking_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (fetchError || !request) throw fetchError || new Error('Request not found');

      if (input.status === 'approved') {
        // Use SECURITY DEFINER function so both coach and player contexts can approve.
        // The function validates the caller is the player or the coach, inserts the
        // coaching_session, and marks the request approved — all atomically.
        const { data: confirmResult, error: confirmError } = await this.supabase
          .rpc('confirm_booking_request', {
            p_request_id: requestId,
            p_response_notes: input.coach_response_notes || null,
          })
          .single();

        if (confirmError) throw confirmError;

        const sessionId = (confirmResult as { session_id: string }).session_id;

        // Fetch the updated request to return
        const { data, error } = await this.supabase
          .from('coaching_booking_requests')
          .select(`
            *,
            player:profiles!coaching_booking_requests_player_id_fkey(
              id, display_name, avatar_url, email
            )
          `)
          .eq('id', requestId)
          .single();

        if (error) throw error;

        // Send confirmation emails + push to both player and coach.
        // Must await — edge function execution stops when the invoker disconnects,
        // which caused push notifications (sent last in the function) to be killed.
        await this.supabase.functions.invoke('send-booking-confirmation', {
          body: { booking_request_id: requestId, session_id: sessionId },
        }).catch(err => console.warn('send-booking-confirmation failed (non-fatal):', err));

        return data as BookingRequest;
      } else {
        // Decline
        const { data, error } = await this.supabase
          .from('coaching_booking_requests')
          .update({
            status: 'declined',
            coach_response_notes: input.coach_response_notes || null,
            responded_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', requestId)
          .select(`
            *,
            player:profiles!coaching_booking_requests_player_id_fkey(
              id, display_name, avatar_url, email
            )
          `)
          .single();

        if (error) throw error;

        // Send cancellation email + push for declined booking
        await this.supabase.functions.invoke('send-cancellation-notification', {
          body: {
            booking_request_id: requestId,
            reason: input.coach_response_notes || null,
            cancelled_by: 'coach',
          },
        }).catch(err => console.warn('send-cancellation-notification failed (non-fatal):', err));


        return data as BookingRequest;
      }
    });
  }

  async getPlayerBookingRequests(
    status?: BookingRequestStatus
  ): Promise<ServiceResponse<BookingRequest[]>> {
    return this.withErrorHandling(async () => {
      const userId = (await this.supabase.auth.getUser()).data.user?.id;
      if (!userId) throw new Error('Not authenticated');

      let query = this.supabase
        .from('coaching_booking_requests')
        .select('*')
        .eq('player_id', userId)
        .order('created_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as BookingRequest[];
    });
  }

  async createBookingRequest(
    input: CreateBookingRequestInput
  ): Promise<ServiceResponse<BookingRequest>> {
    return this.withErrorHandling(async () => {
      // --- Pre-flight validation (must run before INSERT) ---

      if (!input.duration_mins || input.duration_mins <= 0) {
        throw new Error('Session duration is required');
      }

      const { data: authUser } = await this.supabase.auth.getUser();
      const playerId = authUser.user?.id;
      if (!playerId) throw new Error('Not authenticated');

      // Prevent self-booking (coach booking themselves)
      const { data: coachProfile } = await this.supabase
        .from('coach_profiles')
        .select('user_id')
        .eq('id', input.coach_profile_id)
        .maybeSingle();
      if (coachProfile?.user_id === playerId) {
        throw new Error('You cannot book a session with yourself');
      }

      // Fetch booking policies (defaults to 24h/30d if no settings row yet)
      const policiesResult = await this.supabase
        .from('coach_settings')
        .select('min_booking_notice_hours, max_booking_advance_days, auto_accept_bookings, cancellation_window_hours')
        .eq('coach_id', input.coach_profile_id)
        .maybeSingle();

      const policies = policiesResult.data ?? {
        min_booking_notice_hours: 24,
        max_booking_advance_days: 30,
        auto_accept_bookings: true,
        cancellation_window_hours: 24,
      };

      const reqStart = new Date(input.requested_at);
      const hoursUntil = (reqStart.getTime() - Date.now()) / 3_600_000;
      const daysUntil = hoursUntil / 24;

      if (hoursUntil < policies.min_booking_notice_hours) {
        const earliest = new Date(Date.now() + policies.min_booking_notice_hours * 3_600_000);
        throw new Error(
          `This coach requires at least ${policies.min_booking_notice_hours} hours notice. ` +
          `Earliest available: ${earliest.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`
        );
      }

      if (daysUntil > policies.max_booking_advance_days) {
        const latest = new Date(Date.now() + policies.max_booking_advance_days * 24 * 3_600_000);
        throw new Error(
          `Bookings can only be made up to ${policies.max_booking_advance_days} days in advance. ` +
          `Latest available: ${latest.toLocaleString('en-GB', { dateStyle: 'medium' })}`
        );
      }

      // Check for session + time block conflicts
      await this.checkCoachConflict(input.coach_profile_id, reqStart, input.duration_mins);

      // --- INSERT (only runs if all validation passes) ---
      const { data, error } = await this.supabase
        .from('coaching_booking_requests')
        .insert({
          player_id: playerId,
          coach_profile_id: input.coach_profile_id,
          club_id: input.club_id ?? null,
          relationship_id: input.relationship_id || null,
          requested_at: input.requested_at,
          duration_mins: input.duration_mins,
          session_type: input.session_type,
          location: input.location || null,
          focus_areas: input.focus_areas || [],
          player_notes: input.player_notes || null,
          payment_mode: input.payment_mode || null,
          status: 'pending',
        })
        .select(`
          *,
          player:profiles!coaching_booking_requests_player_id_fkey(
            id, display_name, avatar_url, email
          )
        `)
        .single();

      if (error) throw error;
      const bookingRequest = data as BookingRequest;

      // Always auto-confirm — the slot calendar already guarantees the slot is free.
      // The coach controls availability via working hours and time blocks, not post-hoc approval.
      const autoApproved = await this.respondToBookingRequest(bookingRequest.id, { status: 'approved' });
      if (autoApproved.success && autoApproved.data) {
        return autoApproved.data;
      }

      return bookingRequest;
    });
  }

  // ==========================================================================
  // Time Blocks
  // ==========================================================================

  async getTimeBlocks(
    coachProfileId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ServiceResponse<CoachTimeBlock[]>> {
    return this.withErrorHandling(async () => {
      let query = this.supabase
        .from('coach_time_blocks')
        .select('*')
        .eq('coach_profile_id', coachProfileId)
        .order('start_at', { ascending: true });

      if (startDate) query = query.gte('end_at', startDate);
      if (endDate) query = query.lte('start_at', endDate);

      const { data, error } = await query;
      if (error) throw error;
      return data as CoachTimeBlock[];
    });
  }

  async createTimeBlock(
    input: CreateTimeBlockInput
  ): Promise<ServiceResponse<CoachTimeBlock>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coach_time_blocks')
        .insert({
          coach_profile_id: input.coach_profile_id,
          club_id: input.club_id ?? null,
          start_at: input.start_at,
          end_at: input.end_at,
          reason: input.reason || null,
          recurrence: input.recurrence || null,
          recurrence_end_at: input.recurrence_end_at || null,
        })
        .select('*')
        .single();

      if (error) throw error;
      return data as CoachTimeBlock;
    });
  }

  async deleteTimeBlock(id: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { error } = await this.supabase
        .from('coach_time_blocks')
        .delete()
        .eq('id', id);

      if (error) throw error;
    });
  }

  async updateTimeBlock(
    id: string,
    input: Partial<Pick<CreateTimeBlockInput, 'start_at' | 'end_at' | 'reason' | 'recurrence' | 'recurrence_end_at'>>
  ): Promise<ServiceResponse<CoachTimeBlock>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coach_time_blocks')
        .update({
          start_at: input.start_at,
          end_at: input.end_at,
          reason: input.reason || null,
          recurrence: input.recurrence || null,
          ...(input.recurrence_end_at !== undefined && { recurrence_end_at: input.recurrence_end_at || null }),
        })
        .eq('id', id)
        .select('*')
        .single();

      if (error) throw error;
      return data as CoachTimeBlock;
    });
  }

  // ==========================================================================
  // Weekly Availability
  // ==========================================================================

  async getWeeklyAvailability(coachProfileId: string): Promise<ServiceResponse<CoachWeeklyAvailability[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coach_weekly_availability')
        .select('*')
        .eq('coach_profile_id', coachProfileId)
        .order('day_of_week', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CoachWeeklyAvailability[];
    });
  }

  async upsertWeeklyAvailability(rows: UpsertWeeklyAvailabilityInput[]): Promise<ServiceResponse<CoachWeeklyAvailability[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coach_weekly_availability')
        .upsert(
          rows.map(r => ({
            coach_profile_id: r.coach_profile_id,
            day_of_week: r.day_of_week,
            is_available: r.is_available,
            start_time: r.is_available ? (r.start_time ?? null) : null,
            end_time: r.is_available ? (r.end_time ?? null) : null,
          })),
          { onConflict: 'coach_profile_id,day_of_week' }
        )
        .select('*');
      if (error) throw error;
      return (data ?? []) as CoachWeeklyAvailability[];
    });
  }

  // ==========================================================================
  // Group Sessions
  // ==========================================================================

  async createGroupSession(
    input: CreateGroupSessionInput
  ): Promise<ServiceResponse<CoachingSession>> {
    return this.withErrorHandling(async () => {
      // Check for conflicts before inserting
      const reqStart = new Date(input.scheduled_at);
      await this.checkCoachConflict(input.coach_profile_id, reqStart, input.duration_mins);

      // Fetch coach's hourly rate to calculate session price
      const { data: groupCoachProfile, error: groupCoachProfileError } = await this.supabase
        .from('coach_profiles')
        .select('hourly_rate')
        .eq('id', input.coach_profile_id)
        .single();

      if (groupCoachProfileError) {
        console.warn('Could not fetch coach hourly rate:', groupCoachProfileError);
      }

      const groupHourlyRate = groupCoachProfile?.hourly_rate || 0;
      const groupPriceCents = Math.round(groupHourlyRate * (input.duration_mins / 60));
      const groupPaymentStatus = groupPriceCents > 0 ? 'pending' : 'free';

      const primaryPlayerId = input.player_ids[0];
      const primaryRelationshipId = input.relationship_ids?.[primaryPlayerId] || null;

      // Create the session with is_group = true
      const { data: session, error: sessionError } = await this.supabase
        .from('coaching_sessions')
        .insert({
          coach_id: input.coach_profile_id || null,
          player_id: primaryPlayerId || null,
          club_id: input.club_id || null,
          relationship_id: primaryRelationshipId || null,
          session_type: input.session_type,
          scheduled_at: input.scheduled_at,
          duration_mins: input.duration_mins,
          location: input.location || null,
          status: 'scheduled',
          focus_areas: input.focus_areas || [],
          pre_notes: input.pre_notes || null,
          is_group: true,
          max_participants: input.max_participants || input.player_ids.length,
          price_cents: groupPriceCents > 0 ? groupPriceCents : null,
          payment_status: groupPaymentStatus,
          currency: 'EUR',
        })
        .select('*')
        .single();

      if (sessionError) throw sessionError;

      // Insert all participants
      const participants = input.player_ids.map((playerId) => ({
        session_id: session.id,
        player_id: playerId || null,
        relationship_id: input.relationship_ids?.[playerId] || null,
        status: 'confirmed',
      }));

      const { error: participantError } = await this.supabase
        .from('coaching_session_participants')
        .insert(participants);

      if (participantError) throw participantError;

      // Notify all participants via push
      if (input.player_ids.length > 0) {
        const scheduledDate = new Date(input.scheduled_at).toLocaleDateString('en-GB', {
          weekday: 'long', day: 'numeric', month: 'long',
        });
        const scheduledTime = new Date(input.scheduled_at).toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit',
        });
        await Promise.all(
          input.player_ids.map((playerId) =>
            this.sendCoachingPush({
              userId: playerId,
              title: 'Group Session Scheduled',
              body: `You've been added to a group ${input.session_type} on ${scheduledDate} at ${scheduledTime}`,
              type: 'coaching_session',
              data: { session_id: session.id, session_type: input.session_type, scheduled_at: input.scheduled_at },
            }).catch((err: unknown) => console.warn('Group session push failed (non-fatal):', err))
          )
        );
      }

      return session as CoachingSession;
    });
  }

  async getSessionParticipants(
    sessionId: string
  ): Promise<ServiceResponse<SessionParticipant[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coaching_session_participants')
        .select(`
          *,
          player:profiles!coaching_session_participants_player_id_fkey(
            id,
            display_name,
            avatar_url,
            email
          )
        `)
        .eq('session_id', sessionId);

      if (error) throw error;
      return data as SessionParticipant[];
    });
  }

  async addParticipant(
    sessionId: string,
    playerId: string,
    relationshipId?: string
  ): Promise<ServiceResponse<SessionParticipant>> {
    return this.withErrorHandling(async () => {
      // Check max_participants before inserting
      const { data: sessionData } = await this.supabase
        .from('coaching_sessions')
        .select('max_participants, coaching_session_participants(count)')
        .eq('id', sessionId)
        .single();

      if (sessionData?.max_participants) {
        const currentCount = (sessionData.coaching_session_participants as { count: number }[])?.[0]?.count ?? 0;
        if (currentCount >= sessionData.max_participants) {
          throw new Error('This session is at full capacity');
        }
      }

      const { data, error } = await this.supabase
        .from('coaching_session_participants')
        .insert({
          session_id: sessionId,
          player_id: playerId,
          relationship_id: relationshipId || null,
          status: 'confirmed',
        })
        .select(`
          *,
          player:profiles!coaching_session_participants_player_id_fkey(
            id, display_name, avatar_url, email
          )
        `)
        .single();

      if (error) throw error;
      return data as SessionParticipant;
    });
  }

  async removeParticipant(
    sessionId: string,
    playerId: string
  ): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { error } = await this.supabase
        .from('coaching_session_participants')
        .delete()
        .eq('session_id', sessionId)
        .eq('player_id', playerId);

      if (error) throw error;
    });
  }
  // ==========================================================================
  // Coaching Notes
  // ==========================================================================

  /**
   * Get all notes for a player written by the current coach
   */
  async getCoachNotes(coachProfileId: string, playerId: string): Promise<ServiceResponse<CoachNote[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coaching_notes')
        .select(`
          *,
          linked_session:coaching_sessions!coaching_notes_linked_session_id_fkey(
            id,
            session_type,
            scheduled_at,
            focus_areas
          )
        `)
        .eq('coach_id', coachProfileId)
        .eq('player_id', playerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as CoachNote[];
    });
  }

  /**
   * Get notes visible to the player (visibility = 'visible')
   */
  async getPlayerVisibleNotes(playerId: string): Promise<ServiceResponse<CoachNote[]>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coaching_notes')
        .select(`
          *,
          linked_session:coaching_sessions!coaching_notes_linked_session_id_fkey(
            id,
            session_type,
            scheduled_at,
            focus_areas
          )
        `)
        .eq('player_id', playerId)
        .eq('visibility', 'visible')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as CoachNote[];
    });
  }

  /**
   * Create a new coaching note
   */
  async createCoachNote(coachProfileId: string, input: CreateCoachNoteInput): Promise<ServiceResponse<CoachNote>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coaching_notes')
        .insert({
          coach_id: coachProfileId,
          player_id: input.player_id,
          relationship_id: input.relationship_id || null,
          club_id: input.club_id ?? null,
          text: input.text,
          visibility: input.visibility,
          linked_session_id: input.linked_session_id || null,
        })
        .select(`
          *,
          linked_session:coaching_sessions!coaching_notes_linked_session_id_fkey(
            id,
            session_type,
            scheduled_at,
            focus_areas
          )
        `)
        .single();

      if (error) throw error;
      return data as CoachNote;
    });
  }

  /**
   * Update a coaching note
   */
  async updateCoachNote(noteId: string, input: UpdateCoachNoteInput): Promise<ServiceResponse<CoachNote>> {
    return this.withErrorHandling(async () => {
      const { data, error } = await this.supabase
        .from('coaching_notes')
        .update(input)
        .eq('id', noteId)
        .select(`
          *,
          linked_session:coaching_sessions!coaching_notes_linked_session_id_fkey(
            id,
            session_type,
            scheduled_at,
            focus_areas
          )
        `)
        .single();

      if (error) throw error;
      return data as CoachNote;
    });
  }

  /**
   * Delete a coaching note
   */
  async deleteCoachNote(noteId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { error } = await this.supabase
        .from('coaching_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
    });
  }

  // ==========================================================================
  // Private helpers — booking validation
  // ==========================================================================

  /**
   * Check whether the given coach has a conflicting session or time block
   * for the requested slot. Throws with a user-facing message if conflict found.
   *
   * @param coachProfileId  The coach's profile id
   * @param reqStart        Requested session start time
   * @param durationMins    Requested session duration (required — throws if missing)
   */
  private async checkCoachConflict(
    coachProfileId: string,
    reqStart: Date,
    durationMins: number
  ): Promise<void> {
    if (!durationMins || durationMins <= 0) {
      throw new Error('Session duration is required');
    }
    const reqEnd = new Date(reqStart.getTime() + durationMins * 60 * 1000);

    // Fetch all sessions for this coach in a ±4h window (wide enough for long sessions)
    const windowStart = new Date(reqStart.getTime() - 4 * 60 * 60 * 1000);
    const windowEnd = new Date(reqEnd.getTime() + 4 * 60 * 60 * 1000);

    const { data: existingSessions, error: sessErr } = await this.supabase
      .from('coaching_sessions')
      .select('id, scheduled_at, duration_mins')
      .eq('coach_id', coachProfileId)
      .in('status', ['scheduled', 'confirmed'])
      .gte('scheduled_at', windowStart.toISOString())
      .lte('scheduled_at', windowEnd.toISOString());

    if (sessErr) throw sessErr;

    const nowMs = Date.now();
    for (const s of existingSessions ?? []) {
      const sStart = new Date(s.scheduled_at);
      const sEnd = new Date(sStart.getTime() + (s.duration_mins || 60) * 60 * 1000);
      // skip stale sessions whose end time has already passed — they were never closed out
      if (sEnd.getTime() <= nowMs) continue;
      if (sStart < reqEnd && sEnd > reqStart) {
        throw new Error('The coach already has a session booked at that time');
      }
    }

    // Check time blocks (including recurring)
    const blocksResult = await this.getTimeBlocks(
      coachProfileId,
      windowStart.toISOString(),
      windowEnd.toISOString()
    );
    const blocks = blocksResult.success ? (blocksResult.data ?? []) : [];

    for (const block of blocks) {
      const instances = expandRecurringBlock(block, reqStart);
      for (const inst of instances) {
        if (inst.start < reqEnd && inst.end > reqStart) {
          throw new Error('The coach is unavailable during that time');
        }
      }
    }
  }
}

// Export singleton instance
export const sessionsService = new SessionsService();
